/* RegisterTable — the working list, in the mock's table language (13-15: .tsheet, th, td).
 * A white sheet with a sage hairline; quiet sentence-case headers in gray over a sage-dark rule;
 * rows divided by --line. Overdue is the gentle amber wash and chip (D-13-02-1), never red: the
 * chip is re-toned from the Badge's red here, in CSS. Purple is identity ink only (links,
 * focus). Every row selector is scoped under .table: the old bare `tbody tr:hover` leaked into
 * every table in the app (Verify and Release included). */

.tableCard {
  overflow: hidden;
  box-shadow: var(--shadow-sm);
}

/* Horizontal scroll on narrow viewports — the table never breaks the layout. */
.scroll {
  width: 100%;
  overflow-x: auto;
}

.table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

/* ── Header ── */
.th {
  text-align: left;
  padding: 0;
  border-bottom: 1px solid var(--sage-dark);
  background-color: var(--white);
  white-space: nowrap;
  position: sticky;
  top: 0;
}

.sortBtn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  padding: 12px 14px;
  background: none;
  border: none;
  border-radius: 0;
  cursor: pointer;
  font: inherit;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--gray);
  transition: color 0.2s;
}

.th:first-child .sortBtn {
  padding-left: 20px;
}

.sortBtn:hover {
  color: var(--purple-dark);
}

.sortBtn:focus-visible {
  outline: 2px solid var(--purple);
  outline-offset: -2px;
}

.sortArrow {
  font-size: 0.75em;
  color: var(--gray-light);
}

.sortBtn[aria-sort='ascending'] .sortArrow,
.sortBtn[aria-sort='descending'] .sortArrow {
  color: var(--purple-dark);
}

/* ── Body ── */
.td {
  padding: 14px;
  border-bottom: 1px solid var(--line);
  vertical-align: top;
  color: var(--charcoal);
}

.td:first-child {
  padding-left: 20px;
}

.tdAction {
  padding: 14px 20px 14px 14px;
  border-bottom: 1px solid var(--line);
  vertical-align: top;
  text-align: right;
  white-space: nowrap;
}

.table tbody tr:hover > td {
  background-color: #fbfdfc;
}

.table tbody tr:last-child > td {
  border-bottom: none;
}

/* Overdue row: the mock's amber wash from the left edge (.qi.overdue .row). */
.rowOverdue > td:first-child {
  box-shadow: inset 3px 0 0 var(--amber-line);
}

.table tbody tr.rowOverdue > td,
.table tbody tr.rowOverdue:hover > td {
  background-color: var(--amber-soft);
}

/* ── Cell content ── */
.reference {
  font-weight: 600;
  color: var(--charcoal);
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

/* 13-25: the reference opens the record page. */
a.reference {
  color: var(--color-primary-dark);
  text-decoration: underline;
  text-decoration-color: var(--color-border-strong);
  text-underline-offset: 3px;
}

a.reference:hover {
  text-decoration-color: currentColor;
}

.titleCell {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.titleText {
  font-weight: 500;
  line-height: 1.4;
}

/* The Reportable chip beside a title keeps a hairline, so it still reads on the amber overdue wash. */
.titleCell > * + * {
  box-shadow: inset 0 0 0 1px var(--amber-line);
}

.statusCell {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

/* The second badge in the status cell is the "Nd overdue" chip: amber, never red. */
.statusCell > * + * {
  background-color: var(--amber-soft);
  color: var(--amber);
  box-shadow: inset 0 0 0 1px var(--amber-line);
}

.cellText {
  color: var(--gray);
}

/* Risk rating: which column the rating came from ("residual" / "inherent"), small and quiet. */
.ratingBasis {
  font-size: 11.5px;
  color: var(--gray);
  opacity: 0.8;
  margin-left: 2px;
}

/* Created is the last data column in every register's column set: keep a date on one line. */
.td:nth-last-child(2) .cellText {
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

/* ── Record deep link (mock .linkbtn) ── */
.openLink {
  font-size: 13.5px;
  font-weight: 600;
  color: var(--purple-dark);
  text-decoration: underline;
  text-decoration-color: var(--purple-light);
  text-underline-offset: 3px;
  white-space: nowrap;
}

.openLink:hover {
  text-decoration-color: var(--purple-dark);
}

.openLink:focus-visible {
  outline: 2px solid var(--purple);
  outline-offset: 2px;
  border-radius: 6px;
}

/* ── Loading / error / empty ── */
.loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 64px 0;
}

.loadingText {
  color: var(--gray);
  font-size: 14px;
}

.error {
  max-width: 72ch;
  padding: 12px 16px;
  border-radius: 12px;
  background-color: var(--red-soft);
  color: var(--red-dark);
  font-size: 14px;
}

/* The mock's .empty-state: a quiet sheet, a serif line, a plain sentence. */
.empty {
  padding: 36px 24px;
  text-align: center;
}

.emptyTitle {
  margin: 0 0 6px;
  font-family: var(--serif);
  font-weight: 400;
  font-size: 22px;
  line-height: 1.2;
  color: var(--purple-dark);
}

.emptyBody {
  margin: 0 auto;
  max-width: 52ch;
  font-size: 14.5px;
  color: var(--gray);
}
